import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { AppHeader } from "../../components/AppHeader";
import { Loading } from "../../components/Loading";
import { monthName } from "../../lib/dates";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { StarsInline } from "../../components/StarRating";
import { ProductTypeDonut } from "../../components/ProductTypeDonut";
import { KpiDetailModal, KpiCard } from "../../components/KpiDetailModal";

const now = new Date();

function Ring({ pct, color, label, onClick, testid }) {
  const inner = (
    <>
      <div className="relative h-24 w-24">
        <svg viewBox="0 0 36 36" className="h-24 w-24 -rotate-90">
          <circle cx="18" cy="18" r="15.9" fill="none" stroke="#E2E8F0" strokeWidth="3.5" />
          <circle cx="18" cy="18" r="15.9" fill="none" stroke={color} strokeWidth="3.5"
            strokeDasharray={`${pct} ${100 - pct}`} strokeLinecap="round" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="tnum text-xl font-black" style={{ color }}>{pct}%</span>
        </div>
      </div>
      <p className="text-xs font-bold uppercase text-slate-500 mt-2 group-hover:underline underline-offset-4 decoration-dotted">{label}</p>
    </>
  );
  return (
    <button data-testid={testid} onClick={onClick}
      className="flex flex-col items-center group focus:outline-none active:scale-[0.97] transition-transform">
      {inner}
    </button>
  );
}

export default function OwnerReports() {
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [report, setReport] = useState(null);
  const [scorecard, setScorecard] = useState(null);
  const [ptReport, setPtReport] = useState(null);
  const [trend, setTrend] = useState([]);
  const [modal, setModal] = useState(null); // { kind, title, metric, metricLabel, manager_id? }

  useEffect(() => {
    setReport(null);
    api.get("/reports/monthly", { params: { year, month } }).then((r) => setReport(r.data)).catch(() => {});
    api.get("/reports/manager", { params: { year, month } }).then((r) => setScorecard(r.data)).catch(() => {});
    api.get("/reports/product-types", { params: { year, month } }).then((r) => setPtReport(r.data)).catch(() => {});
  }, [year, month]);

  useEffect(() => {
    api.get("/reports/trend").then((r) => setTrend(r.data)).catch(() => {});
  }, []);

  const years = [now.getFullYear(), now.getFullYear() - 1];

  const open = (kind, title, metric = "count", metricLabel = "jobs", extra = {}) =>
    setModal({ kind, title, metric, metricLabel, ...extra });

  return (
    <>
      <AppHeader title="Monthly Result" subtitle="Owner Reports" />
      <div className="px-4 lg:px-8 py-4 space-y-6 lg:max-w-4xl lg:mx-auto">
        {/* Selectors */}
        <div className="grid grid-cols-2 gap-3 lg:max-w-md">
          <select data-testid="report-month" value={month} onChange={(e) => setMonth(parseInt(e.target.value, 10))}
            className="h-12 px-4 rounded-xl border border-slate-300 bg-white outline-none font-semibold">
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <option key={m} value={m}>{monthName(m)}</option>)}
          </select>
          <select data-testid="report-year" value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))}
            className="h-12 px-4 rounded-xl border border-slate-300 bg-white outline-none font-semibold">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>

        {!report ? <Loading /> : (
          <section className="bg-white rounded-2xl border border-slate-200 p-5">
            <h2 className="font-display text-base font-bold text-slate-900 mb-4">{monthName(month)} {year} — Production Result</h2>
            <p className="text-[11px] text-slate-400 mb-3 italic">Tap any number to see the jobs behind it.</p>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <KpiCard testid="kpi-created" value={report.total_created} label="Jobs Created" onClick={() => open("created", "Total Jobs Created", "count", "jobs")} />
              <KpiCard testid="kpi-completed" value={report.total_completed} label="Completed" onClick={() => open("completed", "Completed Jobs", "count", "jobs")} />
            </div>
            <div className="grid grid-cols-2 gap-3 mb-5 rounded-xl bg-slate-50 border border-slate-100 py-3">
              <KpiCard testid="kpi-created-qty" value={(report.created_qty || 0).toLocaleString()} label="Created Qty" onClick={() => open("created", "Total Created Quantity", "qty", "pcs created")} />
              <KpiCard testid="kpi-received-qty" value={(report.received_qty || 0).toLocaleString()} label="Received Qty" color="text-emerald-700" onClick={() => open("received", "Total Received Quantity", "received", "pcs received")} />
            </div>
            <div className="flex justify-around items-center border-t border-slate-100 pt-5">
              <Ring testid="kpi-ontime-ring" pct={report.on_time_pct} color="#15803D" label={`On Time · ${report.on_time}`} onClick={() => open("ontime", "On-Time Jobs", "count", "on-time jobs")} />
              <Ring testid="kpi-breached-ring" pct={report.breach_pct} color="#B91C1C" label={`Breached · ${report.breached}`} onClick={() => open("breached", "Deadline-Breached Jobs", "count", "breached jobs")} />
            </div>
            <div className="grid grid-cols-2 gap-3 mt-5">
              <button data-testid="ontime-list-button" onClick={() => open("ontime", "On-Time Jobs", "count", "on-time jobs")} className="rounded-xl bg-emerald-50 border border-emerald-200 py-3 text-sm font-bold text-emerald-700">On-Time Jobs →</button>
              <button data-testid="breached-list-button" onClick={() => open("breached", "Deadline-Breached Jobs", "count", "breached jobs")} className="rounded-xl bg-red-50 border border-red-200 py-3 text-sm font-bold text-red-700">Breached Jobs →</button>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <KpiCard testid="kpi-avg-breach" value={report.avg_breach_days} label="Avg Breach Days" onClick={() => open("breached", "Average Breach Days", "breach_days", "days average")} />
              <KpiCard testid="kpi-max-breach" value={report.max_breach_days} label="Max Breach Days" onClick={() => open("breached", "Max Breach Days", "breach_days", "max days")} />
            </div>
          </section>
        )}

        {/* Product type production donut */}
        {ptReport && (
          <section>
            <h2 className="font-display text-sm font-bold uppercase tracking-wider text-slate-500 mb-3">
              Product Type Production · {monthName(month)} {year}
            </h2>
            <ProductTypeDonut data={ptReport} testid="reports-product-type-donut" />
          </section>
        )}

        {/* Manager scorecard */}
        {scorecard && (
          <section className="bg-white rounded-2xl border border-slate-200 p-5">
            <h2 className="font-display text-base font-bold text-slate-900 mb-1">{scorecard.manager_name}</h2>
            <p className="text-xs text-slate-400 mb-4 uppercase font-bold">Scorecard · {monthName(month)}</p>
            <div className="grid grid-cols-3 gap-2 mb-4">
              <KpiCard testid="kpi-score-done" value={scorecard.completed} label="Done" onClick={() => open("completed", `${scorecard.manager_name} — Completed`, "count", "jobs", { manager_id: scorecard.manager_id })} />
              <KpiCard testid="kpi-score-ontime" value={scorecard.on_time} label="On Time" color="text-emerald-600" onClick={() => open("ontime", `${scorecard.manager_name} — On-Time`, "count", "jobs", { manager_id: scorecard.manager_id })} />
              <KpiCard testid="kpi-score-breached" value={scorecard.breached} label="Breached" color="text-red-600" onClick={() => open("breached", `${scorecard.manager_name} — Breached`, "count", "jobs", { manager_id: scorecard.manager_id })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-emerald-50 py-2 text-center"><p className="tnum text-lg font-black text-emerald-700">{scorecard.on_time_pct}%</p><p className="text-[9px] font-bold uppercase text-emerald-600">On-Time</p></div>
              <div className="rounded-xl bg-red-50 py-2 text-center"><p className="tnum text-lg font-black text-red-700">{scorecard.breach_pct}%</p><p className="text-[9px] font-bold uppercase text-red-600">Breach</p></div>
            </div>

            <button data-testid="kpi-quality" onClick={() => open("rated", `${scorecard.manager_name} — Quality Score`, "rating", "average rating", { manager_id: scorecard.manager_id })}
              className="w-full mt-4 pt-4 border-t border-slate-100 text-left active:scale-[0.99] transition-transform focus:outline-none">
              <p className="text-xs font-bold uppercase text-slate-400 mb-2">Manager Quality <span className="text-slate-300 normal-case">(tap for ratings)</span></p>
              {scorecard.avg_rating != null ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <StarsInline value={scorecard.avg_rating} size="h-5 w-5" />
                    <span className="tnum font-black text-amber-600">{scorecard.avg_rating} / 5</span>
                  </div>
                  <div className="text-right">
                    <p className="tnum text-xl font-black text-amber-600">{scorecard.quality_pct}%</p>
                    <p className="text-[9px] font-bold uppercase text-slate-400">{scorecard.jobs_rated} jobs rated</p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-400 font-medium">No quality ratings yet</p>
              )}
              <div className="mt-2 h-2.5 w-full rounded-full bg-slate-200 overflow-hidden">
                <div className="h-full rounded-full bg-amber-400 transition-all" style={{ width: `${scorecard.quality_pct || 0}%` }} />
              </div>
            </button>
          </section>
        )}

        {/* Trend */}
        {trend.length > 0 && (
          <section className="bg-white rounded-2xl border border-slate-200 p-5">
            <h2 className="font-display text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">On-Time Trend</h2>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={trend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => `${v}%`} />
                <Line type="monotone" dataKey="on_time_pct" stroke="#C86D51" strokeWidth={3} dot={{ r: 4, fill: "#9A462B" }} />
              </LineChart>
            </ResponsiveContainer>
          </section>
        )}
      </div>

      <KpiDetailModal open={!!modal} onClose={() => setModal(null)}
        kind={modal?.kind} year={year} month={month}
        manager_id={modal?.manager_id}
        title={modal?.title} metric={modal?.metric} metricLabel={modal?.metricLabel} />
    </>
  );
}
